import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import toast from 'react-hot-toast';
import { KeyRound, Check, Eye, EyeOff } from 'lucide-react';

/**
 * Forced-password-change page used both for first-login (must_change_password)
 * and voluntary password changes. The route gate in App.js forces logged-in
 * users with must_change_password=true to this page and blocks all others.
 */
export default function ChangePasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const isForced = !!user?.must_change_password;
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverErr, setServerErr] = useState(null);

  const rules = [
    { ok: newPw.length >= 8, label: t('changePassword.ruleMinLength') },
    { ok: /\d/.test(newPw), label: t('changePassword.ruleDigit') },
    { ok: /[^A-Za-z0-9]/.test(newPw), label: t('changePassword.ruleSpecial') },
    { ok: newPw.length > 0 && newPw === confirmPw, label: t('changePassword.ruleMatch') },
  ];
  const allOk = rules.every((r) => r.ok) && (isForced || currentPw.length > 0);

  async function submit(e) {
    e.preventDefault();
    if (!allOk) return;
    setServerErr(null);
    setSubmitting(true);
    try {
      await api.post('/api/auth/change-password', {
        current_password: isForced ? undefined : currentPw,
        new_password: newPw,
        new_password_confirm: confirmPw,
      });
      toast.success(t('changePassword.success'));
      // Refresh user (clears must_change_password) and route to dashboard.
      if (refresh) await refresh();
      navigate('/heute', { replace: true });
    } catch (err) {
      const code = err.response?.data?.error;
      if (code === 'invalid_credentials') setServerErr(t('changePassword.errors.wrongCurrent'));
      else if (code === 'password_reuse') setServerErr(t('changePassword.errors.reuse'));
      else if (code === 'passwords_mismatch') setServerErr(t('changePassword.errors.mismatch'));
      else if (code === 'invalid_input') setServerErr(t('changePassword.errors.invalid'));
      else setServerErr(err.response?.data?.message || t('errors.unknown'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-start justify-center pt-16 px-4" data-testid="change-password-page">
      <div className="so-card w-full max-w-md p-6">
        <div className="flex items-center gap-2 text-navy">
          <KeyRound size={18}/>
          <h1 className="text-lg font-semibold">{t('changePassword.title')}</h1>
        </div>
        {isForced && (
          <p className="mt-2 text-sm text-text" data-testid="change-pw-forced-notice">
            {t('changePassword.forcedNotice')}
          </p>
        )}
        <form onSubmit={submit} className="mt-4 space-y-3">
          {!isForced && (
            <Field label={t('changePassword.currentLabel')}
              input={
                <input
                  type={show ? 'text' : 'password'}
                  className="so-input"
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  autoComplete="current-password"
                  data-testid="current-password-input"
                />
              }
            />
          )}
          <Field label={t('changePassword.newLabel')}
            input={
              <div className="relative">
                <input
                  type={show ? 'text' : 'password'}
                  className="so-input pr-10"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  autoComplete="new-password"
                  data-testid="new-password-input"
                />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute inset-y-0 right-2 text-text-secondary hover:text-text" aria-label="toggle visibility" data-testid="toggle-show-password">
                  {show ? <EyeOff size={14}/> : <Eye size={14}/>}
                </button>
              </div>
            }
          />
          <Field label={t('changePassword.confirmLabel')}
            input={
              <input
                type={show ? 'text' : 'password'}
                className="so-input"
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password"
                data-testid="confirm-password-input"
              />
            }
          />
          <ul className="mt-2 space-y-1 text-xs" data-testid="password-rules">
            {rules.map((r, i) => (
              <li key={i} className={`flex items-center gap-1.5 ${r.ok ? 'text-success' : 'text-text-secondary'}`}>
                <Check size={12} className={r.ok ? 'opacity-100' : 'opacity-30'}/>
                {r.label}
              </li>
            ))}
          </ul>
          {serverErr && <p className="text-xs text-danger" data-testid="change-pw-server-error">{serverErr}</p>}
          <button type="submit" disabled={!allOk || submitting} className="so-btn so-btn-primary w-full mt-4" data-testid="change-password-submit">
            {submitting ? t('changePassword.submitting') : t('changePassword.submit')}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, input }) {
  return (
    <div>
      <label className="so-label">{label}</label>
      {input}
    </div>
  );
}
